import { FC } from 'react';
import { navigate } from 'gatsby';
import { useQueryClient } from 'react-query';
import { ButtonPrimary, Form } from '@lambdacurry/component-library';
import { useMeQuery, useUpdatePreferencesMutation } from 'lib/home/generated/queries';
import { MeQuery, SubscriptionStatus } from 'lib/home/generated/types/api';
import { RegistrationRouteMap, useRegistrationStore } from '../../../store';
import {
  allergens,
  buildPreferencesFromValues,
  primaryDiets,
  primaryDietsLabelMap,
  secondaryOptions
} from './PreferencesForm.helpers';
import { PreferencesFormSection } from './PreferencesFormSection';
import './preferences-form-section.scss';

export interface PreferencesFormValues {
  primaryDiet: string;
  secondaryOptions: { [x: string]: boolean };
  allergens: { [x: string]: boolean };
}

export const PreferencesForm: FC = () => {
  const { data: user, isError } = useMeQuery();
  const { preferences, setPreferences } = useRegistrationStore();
  const queryClient = useQueryClient();
  const currentPreferences = user?.me?.meal_preferences ?? preferences;

  const { mutate: updatePreferencesMutation } = useUpdatePreferencesMutation({
    onSuccess: data => {
      queryClient.setQueryData(['Me'], (query: MeQuery) => {
        query.me.meal_preferences = data.updatePreferences;
        return query;
      });

      if (!user?.me?.subscription || user?.me?.subscription?.status === SubscriptionStatus.Onboarding) {
        navigate(RegistrationRouteMap.customizeBox);
        return;
      }

      navigate('/account');
      return;
    }
  });

  const initialValues: PreferencesFormValues = {
    primaryDiet: currentPreferences.diets
      ? currentPreferences.diets.find(diet => Object.keys(primaryDietsLabelMap).includes(diet))
      : null,
    secondaryOptions: {
      ...secondaryOptions.reduce(
        (acc, curr) => ({
          ...acc,
          [curr.value]: currentPreferences?.diets ? currentPreferences.diets.includes(curr.value) : false
        }),
        {}
      )
    },
    allergens: {
      ...allergens.reduce(
        (acc, curr) => ({
          ...acc,
          [curr.value]: currentPreferences?.allergens ? currentPreferences.allergens.includes(curr.value) : false
        }),
        {}
      )
    }
  };

  const handleSubmit = (values: PreferencesFormValues) => {
    const isAuthorized = !isError && !!user;
    const preferences = buildPreferencesFromValues(values);

    if (isAuthorized) {
      updatePreferencesMutation({ updatePreferencesInput: preferences });
      return;
    }

    setPreferences(preferences);
    navigate(RegistrationRouteMap.createAccount);
  };

  return (
    <Form enableReinitialize initialValues={initialValues} onSubmit={handleSubmit}>
      {formikProps => {
        return (
          <>
            <PreferencesFormSection
              formikProps={formikProps}
              name="primaryDiet"
              options={primaryDiets}
              type="radio"
              className="!mt-0"
              title={
                <>
                  What are your <span className="font-medium">meal preferences</span>?
                </>
              }
            />

            <PreferencesFormSection
              name="secondaryOptions"
              options={secondaryOptions}
              type="checkbox"
              title={
                <>
                  Are you looking for any of these <span className="font-medium">options</span>?
                </>
              }
            />

            <PreferencesFormSection
              name="allergens"
              options={allergens}
              type="checkbox"
              title={
                <>
                  What are you <span className="font-medium">allergic</span> to?
                </>
              }
            />

            <ButtonPrimary
              className="w-full md:w-56"
              data-cy="submit-button"
              disabled={!formikProps.values.primaryDiet}
              type="submit"
            >
              Continue
            </ButtonPrimary>
          </>
        );
      }}
    </Form>
  );
};
